import { AddressFieldMaxLength } from "@shared";
import { TFunction } from "i18next";
import * as Yup from "yup";

import { EmailPattern, ZipCodePattern } from "@/common/helper/validationPatterns";

import { UsernamePattern, VolunteerFormType, VolunteerMaxLength, VolunteerMinLength } from "./VolunteerForm.types";

type VolunteerValidationType = {
  [Property in keyof VolunteerFormType]?: Yup.AnySchema | Yup.Reference;
};

export function volunteerValidationSchema(t: TFunction) {
  return Yup.object().shape<VolunteerValidationType>({
    // Kept in sync with the backend's Volunteer.UsernameMinLength / UsernamePattern: the Kürzel
    // becomes the Keycloak username, and the portal account locks it once access is granted.
    username: Yup.string()
      .required(t("common.validation.fieldRequired", { fieldName: t("volunteers.form.username") }))
      .min(VolunteerMinLength.username, ({ min }) => t("common.validation.minLength", { min }))
      .matches(UsernamePattern, { message: t("volunteers.form.invalidUsername"), excludeEmptyString: true })
      .max(VolunteerMaxLength.username, ({ max }) => t("common.validation.maxLength", { max })),
    salutation: Yup.string()
      .nullable()
      .max(VolunteerMaxLength.salutation, ({ max }) => t("common.validation.maxLength", { max })),
    title: Yup.string()
      .nullable()
      .max(VolunteerMaxLength.title, ({ max }) => t("common.validation.maxLength", { max })),
    lastName: Yup.string()
      .required(t("common.validation.fieldRequired", { fieldName: t("volunteers.form.lastName") }))
      .max(VolunteerMaxLength.lastName, ({ max }) => t("common.validation.maxLength", { max })),
    firstName: Yup.string()
      .required(t("common.validation.fieldRequired", { fieldName: t("volunteers.form.firstName") }))
      .max(VolunteerMaxLength.firstName, ({ max }) => t("common.validation.maxLength", { max })),
    street: Yup.string()
      .required(t("common.validation.fieldRequired", { fieldName: t("common.address.street") }))
      .max(AddressFieldMaxLength.street, ({ max }) => t("common.validation.maxLength", { max })),
    houseNumber: Yup.string()
      .required(t("common.validation.fieldRequired", { fieldName: t("common.address.houseNumber") }))
      .max(AddressFieldMaxLength.houseNumber, ({ max }) => t("common.validation.maxLength", { max })),
    zipCode: Yup.string()
      .required(t("common.validation.fieldRequired", { fieldName: t("common.address.zipCode") }))
      .matches(ZipCodePattern, { message: t("volunteers.form.invalidZipCode"), excludeEmptyString: true })
      .min(4, ({ min }) => t("common.validation.minLength", { min }))
      .max(AddressFieldMaxLength.zipCode, ({ max }) => t("common.validation.maxLength", { max })),
    city: Yup.string()
      .required(t("common.validation.fieldRequired", { fieldName: t("common.address.city") }))
      .max(AddressFieldMaxLength.city, ({ max }) => t("common.validation.maxLength", { max })),
    country: Yup.string()
      .required(t("common.validation.fieldRequired", { fieldName: t("common.address.country") }))
      .max(AddressFieldMaxLength.country, ({ max }) => t("common.validation.maxLength", { max })),
    email: Yup.string()
      .required(t("common.validation.fieldRequired", { fieldName: t("volunteers.form.email") }))
      .matches(EmailPattern, {
        message: t("volunteers.form.invalidEmail"),
        excludeEmptyString: true,
      })
      .max(VolunteerMaxLength.email, ({ max }) => t("common.validation.maxLength", { max })),
    phone: Yup.string()
      .nullable()
      .max(VolunteerMaxLength.phone, ({ max }) => t("common.validation.maxLength", { max })),
    remarks: Yup.string().nullable(),
  });
}
